// FileUploadContext.jsx
// Responsibility: Managing file attachments for the current prompt.
//
// Attachments travel INSIDE the chat request: each file is base64-encoded
// locally and sent as an inline content block on /v1/chat/completions — the
// same public-API contract SDK consumers use (dogfooding rule). There is no
// separate upload step any more; the old Cognito presign → S3 → /app/uploads
// flow was decommissioned with FND-6.
import React, { createContext, useContext, useState, useCallback } from 'react';
import { encodeFile, validateBatch, AttachmentValidationError } from '../attachments';
import { useAuth } from './AuthContext';
import { useChatSession } from './ChatSessionContext';

const FileUploadContext = createContext();

export const FileUploadProvider = ({ children }) => {
    // --- File Attachment States ---
    const [pendingFiles, setPendingFiles] = useState([]);
    const [uploadingFiles, setUploadingFiles] = useState([]);
    const [webSearchEnabled, setWebSearchEnabled] = useState(false);
    const [uploadError, setUploadError] = useState(null);

    // Get contexts
    const { userId, isSessionValid, setAuthError } = useAuth();
    const { submitMessage, inputValue, setInputValue, isWaitingForResponse, isLimitReached } = useChatSession();

    // --- Add Files ---
    const addFiles = useCallback((files) => {
        console.log(`FileUploadContext: User ${userId} adding ${files.length} files`);

        setUploadError(null);

        const newFiles = files.map(file => ({
            id: crypto.randomUUID(),
            file: file,
            status: 'pending',
            progress: 0,
            userId: userId // Track file owner
        }));

        setPendingFiles(prev => [...prev, ...newFiles]);
    }, [userId]);

    // --- Remove File ---
    const removeFile = useCallback((fileId) => {
        console.log(`FileUploadContext: Removing file ${fileId}`);
        setPendingFiles(prev => prev.filter(f => f.id !== fileId));

        // Also remove from encoding list if it's there
        setUploadingFiles(prev => prev.filter(id => id !== fileId));
    }, []);

    // --- Encode Files (local, no network) ---
    const encodeFiles = async (files) => {
        console.log(`FileUploadContext: Encoding ${files.length} files for the chat request`);

        setUploadError(null);
        validateBatch(files.map(f => f.file));

        const encodePromises = files.map(async (fileInfo) => {
            try {
                setUploadingFiles(prev => [...prev, fileInfo.id]);
                setPendingFiles(prev => prev.map(f =>
                    f.id === fileInfo.id
                        ? { ...f, status: 'uploading', progress: 50 }
                        : f
                ));

                const encoded = await encodeFile(fileInfo.file);

                setPendingFiles(prev => prev.map(f =>
                    f.id === fileInfo.id
                        ? { ...f, status: 'uploaded', progress: 100 }
                        : f
                ));

                return encoded;
            } catch (error) {
                console.error(`FileUploadContext: Encoding failed for ${fileInfo.file.name}:`, error);
                setUploadError(error.message);
                setPendingFiles(prev => prev.map(f =>
                    f.id === fileInfo.id
                        ? { ...f, status: 'error', error: error.message }
                        : f
                ));
                throw error;
            } finally {
                setUploadingFiles(prev => prev.filter(id => id !== fileInfo.id));
            }
        });

        return Promise.all(encodePromises);
    };

    // --- Handle Submit with Files ---
    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        // Check authentication
        if (!isSessionValid) {
            setAuthError('Your session has expired. Please sign in again.');
            return;
        }

        if (isLimitReached || (!inputValue.trim() && pendingFiles.length === 0) || isWaitingForResponse) {
            console.log('FileUploadContext: Submit blocked - validation failed');
            return;
        }

        const userPrompt = inputValue.trim();

        let attachments = [];

        if (pendingFiles.length > 0) {
            try {
                attachments = await encodeFiles(pendingFiles);
            } catch (error) {
                if (!(error instanceof AttachmentValidationError)) {
                    console.error('FileUploadContext: File encoding failed:', error);
                }
                // Don't submit if encoding failed; the error banner explains why.
                setUploadError(error.message);
                return;
            }
        }

        // Clear input immediately (optimistic — message will appear in chat)
        setInputValue('');

        // Submit message with the encoded attachments
        try {
            await submitMessage(userPrompt, attachments, webSearchEnabled);
            // Only clear files after the message was successfully submitted.
            // If the LLM call fails, files are preserved so the user can retry
            // without re-attaching.
            setPendingFiles([]);
            setUploadError(null);
            console.log('FileUploadContext: Message submitted successfully');
        } catch (error) {
            console.error('FileUploadContext: Failed to submit message:', error);
            // Error is displayed in chat by ChatSessionContext
        }
    };

    // --- Get Upload Status ---
    const getUploadStatus = useCallback(() => {
        const total = pendingFiles.length;
        const uploading = pendingFiles.filter(f => f.status === 'uploading').length;
        const uploaded = pendingFiles.filter(f => f.status === 'uploaded').length;
        const errors = pendingFiles.filter(f => f.status === 'error').length;

        return {
            total,
            uploading,
            processing: 0,
            uploaded,
            errors,
            isUploading: uploading > 0,
            hasErrors: errors > 0,
            allUploaded: uploaded === total && total > 0
        };
    }, [pendingFiles]);

    // --- Reset File Upload State ---
    const resetFileUpload = useCallback(() => {
        console.log('FileUploadContext: Resetting file upload state');
        setPendingFiles([]);
        setUploadingFiles([]);
        setUploadError(null);
        setWebSearchEnabled(false);
    }, []);

    const value = {
        // State
        pendingFiles,
        uploadingFiles,
        webSearchEnabled,
        uploadError,

        // Actions
        addFiles,
        removeFile,
        setUploadError,
        setWebSearchEnabled,
        handleSubmit,
        getUploadStatus,
        resetFileUpload
    };

    return (
        <FileUploadContext.Provider value={value}>
            {children}
        </FileUploadContext.Provider>
    );
};

export const useFileUpload = () => {
    const context = useContext(FileUploadContext);
    if (context === undefined) {
        throw new Error('useFileUpload must be used within a FileUploadProvider');
    }
    return context;
};
